import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import SVG from 'components/base/SVG';

import { POPUP_HOLDER_OPEN_CLASSNAME } from "../../../app/PopupHolder/constants";

import css from "./style.scss";

// Renders a simple modal body UI with title, body, footer and gradient overlay background
// Only takes care of modal closing logic on esc / body click
// Modal open logic is handled via parent component using PopupHolder
const SimpleModal = ({
  title,
  isOpen,
  wrapperClassName,
  modalTitleClassName,
  modalBodyClassName,
  modalFooterWrapperClassName,
  onCancel,
  onSubmit,
  onCancelRedirect,
  onSubmitRedirect,
  customHeader,
  customFooter,
  cancelText,
  submitText,
  uniqueId = `simple-modal-${new Date().getTime()}`,
  modalCancelBtnClassName,
  modalSubmitBtnClassName,
  description,
  modalHeaderWrapperClassName,
  ignoreEscAndClickOutside = false,
  modalDescriptionClassName,
  modalBody,
  modalClassName,
  loading = false,
}) => {
  const modalRef = React.useRef(null);

  // Handles cancel button click
  const cancel = React.useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    if (onCancelRedirect) {
      onCancelRedirect();
    }
  }, [onCancelRedirect]);

  // Handles submit button click
  const submit = () => {
    if (onSubmit) {
      onSubmit();
    }
    if (onSubmitRedirect) {
      onSubmitRedirect();
    }
  };

  // Handles modal closing logic on esc / body click
  // `ignoreEscAndClickOutside` belongs in this effect's deps: a caller that
  // toggles it dynamically after mount (e.g. suppressing outside-click while
  // a popup stacked on top of this one is open) needs the listener attached
  // or torn down to follow, not fixed by whatever the prop was at mount time.
  // Every existing caller passes a value that never changes for a given
  // modal's lifetime, so this doesn't change their behavior — the effect
  // still only ever runs once for them.
  React.useEffect(() => {
    if (ignoreEscAndClickOutside) return undefined;
    document.body.classList.add(POPUP_HOLDER_OPEN_CLASSNAME);
    const handleKeyDown = (e) => e.key === "Escape" && cancel();
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) cancel();
    };
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.body.classList.remove(POPUP_HOLDER_OPEN_CLASSNAME);
    };
  }, [cancel, ignoreEscAndClickOutside]);

  return (
    <div
      className={classNames(css.clsSimpleModalWrapper, wrapperClassName)}
      id={uniqueId}
    >
      <div
        className={classNames(css.clsSimpleModal, modalClassName)}
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {customHeader && (
          <div
            className={classNames(
              css.clsSimpleModalHeader,
              modalHeaderWrapperClassName
            )}
          >
            {customHeader}
          </div>
        )}
        {!customHeader && title && (
          <div
            className={classNames(
              css.clsSimpleModalHeader,
              modalHeaderWrapperClassName
            )}
          >
            <h3
              className={classNames(
                css.clsSimpleModalTitle,
                modalTitleClassName
              )}
            >
              {title || "Default Title"}
            </h3>
          </div>
        )}
        {modalBody && (
          <div
            className={classNames(css.clsSimpleModalBody, modalBodyClassName)}
          >
            {modalBody}
          </div>
        )}
        {description && (
          <div
            className={classNames(css.clsSimpleModalBody, modalBodyClassName)}
          >
            <p
              className={classNames(
                css.clsSimpleModalDescription,
                modalDescriptionClassName
              )}
            >
              {description}
            </p>
          </div>
        )}
        {customFooter && (
          <div
            className={classNames(
              css.clsSimpleModalFooter,
              modalFooterWrapperClassName
            )}
          >
            {customFooter}
          </div>
        )}
        {!customFooter && (
          <div
            className={classNames(
              css.clsSimpleModalFooter,
              modalFooterWrapperClassName
            )}
          >
            <button
              onClick={cancel}
              className={classNames(
                css.clsModalCancelBtn,
                modalCancelBtnClassName
              )}
              disabled={loading}
            >
              {cancelText || "Cancel"}
            </button>

            {onSubmit && (
              <button
                onClick={submit}
                className={classNames(
                  css.clsModalSubmitBtn,
                  modalSubmitBtnClassName
                )}
                disabled={loading}
              >
                {loading ? <SVG icon="spinner" className={css.spinner} /> : (submitText || "Submit")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleModal;

SimpleModal.propTypes = {
  /** Modal title */
  title: PropTypes.string,
  /** Submit button text */
  submitText: PropTypes.string,
  /** Cancel button text */
  cancelText: PropTypes.string,
  /** Submit button click handler */
  onSubmit: PropTypes.func,
  /** Cancel button click handler */
  onCancel: PropTypes.func,
  /** Modal open state */
  isOpen: PropTypes.bool,
  /** Modal body content expected when description is not provided */
  modalBody: PropTypes.node,
  /** Modal header component */
  customHeader: PropTypes.node,
  /** Modal footer component   */
  customFooter: PropTypes.node,
  /** Modal unique id */
  uniqueId: PropTypes.string.isRequired,
  /** Submit button redirect handler */
  onSubmitRedirect: PropTypes.func,
  /** Cancel button redirect handler */
  onCancelRedirect: PropTypes.func,
  /** Modal wrapper class name */
  wrapperClassName: PropTypes.string,
  /** Modal body class name */
  modalBodyClassName: PropTypes.string,
  /** Modal header class name */
  modalHeaderWrapperClassName: PropTypes.string,
  /** Modal footer class name */
  modalFooterWrapperClassName: PropTypes.string,
  /** Submit button class name */
  modalSubmitBtnClassName: PropTypes.string,
  /** Cancel button class name */
  modalCancelBtnClassName: PropTypes.string,
  /** Modal description expected when body is not provided */
  description: PropTypes.string,
  /** Modal description class name */
  modalDescriptionClassName: PropTypes.string,
  /** Ignore ESC and click outside modal closing logic */
  ignoreEscAndClickOutside: PropTypes.bool,
  /** Modal class name */
  modalClassName: PropTypes.string,
  /** Loading state to disable buttons and show loader in submit button */
  loading: PropTypes.bool,
};
